/**
 * Table-argument lookups used by every consumer that validates or prints a
 * code. Each takes the resolved tables first so a consumer with a tenant
 * passes its own tables and one without passes DGI_PARAMETER_TABLES.
 */
import { debitNoteCommentLineRowCodes } from "../constants/debit-note.js";
import { DGI_PARAMETER_TABLES, tableRows } from "./dgi-tables.js";
// ── Client types ─────────────────────────────────────────────────────────
export function findClientType(tables, code) {
    return code ? tables.clientTypes.find((row) => row.code === code) : undefined;
}
export function isValidClientTypeCode(tables, code) {
    return typeof code === "string" && findClientType(tables, code) !== undefined;
}
/** Codes of the table, DGI first, for validation messages. */
export function clientTypeCodes(tables) {
    return tables.clientTypes.map((row) => row.code);
}
/** What an invoice must carry for the client type, or undefined if unknown. */
export function clientRequirements(tables, code) {
    return findClientType(tables, code)?.attributes;
}
// ── Article types ────────────────────────────────────────────────────────
export function findArticleType(tables, code) {
    return code ? tables.articleTypes.find((row) => row.code === code) : undefined;
}
export function isValidArticleTypeCode(tables, code) {
    return typeof code === "string" && findArticleType(tables, code) !== undefined;
}
export function articleTypeCodes(tables) {
    return tables.articleTypes.map((row) => row.code);
}
/** False for an unknown code: only a known service posts as a service. */
export function isServiceArticleType(tables, code) {
    return findArticleType(tables, code)?.attributes.isService ?? false;
}
// ── Credit-note natures ──────────────────────────────────────────────────
export function findCreditNoteType(tables, code) {
    return code ? tables.creditNoteTypes.find((row) => row.code === code) : undefined;
}
export function isValidCreditNoteTypeCode(tables, code) {
    return typeof code === "string" && findCreditNoteType(tables, code) !== undefined;
}
export function creditNoteTypeCodes(tables) {
    return tables.creditNoteTypes.map((row) => row.code);
}
/** True for an unknown code: a nature must be known to stand alone. */
export function creditNoteRequiresOriginal(tables, code) {
    return findCreditNoteType(tables, code)?.attributes.requiresOriginalInvoice ?? true;
}
// ── Comment lines ────────────────────────────────────────────────────────
export function findCommentLineType(tables, code) {
    return code ? tables.commentLineTypes.find((row) => row.code === code) : undefined;
}
/** True for A–H (the annex fixes the codes; only labels vary per tenant). */
export function isValidCommentLineCode(tables, code) {
    return typeof code === "string" && findCommentLineType(tables, code) !== undefined;
}
/** Lines offered at entry: every line that carries a label. */
export function availableCommentLineTypes(tables) {
    return tables.commentLineTypes.filter((row) => !row.attributes.isReserved);
}
/**
 * Lines an operator types by hand: the available lines less the rows the
 * debit-note lines print under (« Note de débit », « Motif »), which only
 * the debit-note service writes.
 */
export function manualCommentLineTypes(tables) {
    const debitNoteCodes = debitNoteCommentLineRowCodes(tables.commentLineTypes);
    return availableCommentLineTypes(tables).filter((row) => !debitNoteCodes.includes(row.code));
}
// ── Printing ─────────────────────────────────────────────────────────────
/**
 * Mention printed for a code: the row's mention, else its label, else the
 * bare code between brackets so an unknown code still shows on the document.
 */
export function invoiceMention(tables, table, code) {
    const row = tableRows(tables, table).find((candidate) => candidate.code === code);
    return row?.invoiceMention ?? row?.label ?? `[${code}]`;
}
/** The DGI floor, for callers without a tenant. */
export function defaultParameterTables() {
    return DGI_PARAMETER_TABLES;
}
//# sourceMappingURL=helpers.js.map